import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AppConfigService } from '@app/app-config.service';
import { Icons } from '@app/shared/constants';
import { FileTypeDocument, FileTypeImage, FileTypePresentation } from '@app/shared/models/file.interface';
import { ButtonColor, ButtonMode } from '@components/buttons/button/button.component';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { catchError, last, map } from 'rxjs/operators';

export class FileUploadModel {
  data: File;
  type: string;
  state: string;
  inProgress: boolean;
  progress: number;
  hasError?: boolean;
  errorMessage?: string;
  canCancel?: boolean;
  canRetry?: boolean;
  sub?: Subscription;
  urlPreview?: string | ArrayBuffer;
}

export interface FileTypes {
  images?: FileTypeImage[];
  documents?: FileTypeDocument[];
  presentations?: FileTypePresentation[];
}

// The types of files allowed to upload images
export const ImagesAllowed = `.${FileTypeImage.JPEG},.${FileTypeImage.JPG},.${FileTypeImage.PNG}`;

@Component({
  selector: 'vfs-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('in', style({ opacity: 100 })),
      transition('* => void', [animate(300, style({ opacity: 0 }))])
    ])
  ]
})
export class UploadComponent implements OnInit, OnDestroy {
  /** If the upload is direct */
  @Input() directUpload = true;
  /** Link text */
  @Input() label = 'core.general.upload';
  /** Name used in form which will be sent in HTTP request. */
  @Input() param = 'file';
  /** The module that uploads the file (e.g. User, Library, Product...) */
  @Input() module = '';
  /** If multiple files can be uploaded at once */
  @Input() multiple = true;
  /** Specify what file types the user can pick from the file input dialog box */
  @Input() accept: string;
  /** If you want to have a preview of the file upload */
  @Input() preview = false;
  /** If you want to have a preview of the files (if they are images) */
  @Input() previewImages = false;
  /** File types not allowed */
  @Input() blacklist: (string | FileTypeImage | FileTypeDocument | FileTypePresentation)[] = [];
  /** File types allowed */
  @Input() whitelist: (string | FileTypeImage | FileTypeDocument | FileTypePresentation)[] = [];
  /** Allow you to add handler after its completion. Bubble up response text from remote. */
  @Input() mode: ButtonMode = ButtonMode.PRIMARY;
  /** Select the button mode style */
  @Input() showIcon = true;
  /** Disabled upload button */
  @Input() disabled: boolean;
  @Output() uploadComplete = new EventEmitter<any>();
  @Output() fileNameUploaded = new EventEmitter<any>();
  @Output() uploadStarted = new EventEmitter<any>();
  @ViewChild('inputUpload') inputUpload: ElementRef;
  files: FileUploadModel[] = [];
  buttonColors = ButtonColor;

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    if (this.whitelist.length >= 1 && this.blacklist.length >= 1) {
      console.warn('If there are file types in the whitelist it is unnecessary to add in the blacklist');
    }
    this.whitelist.forEach(fileType => {
      if (this.blacklist.includes(fileType)) {
        console.warn('There are elements in the blacklist that are also in the whitelist. The blacklist has priority');
      }
    });
  }

  /**
   * Destroy subscriptions
   */
  ngOnDestroy(): void {
    this.files.forEach(file => {
      file.sub.unsubscribe();
    });
  }

  /**
   * Function that is executed by pressing the button, which activates the input file and detects the changes in it to upload the files
   */
  onClick() {
    this.inputUpload.nativeElement.onchange = () => {
      for (const file of this.inputUpload.nativeElement.files) {
        let fileToUpload: FileUploadModel = {
          data: file,
          type: file.type,
          state: 'in',
          inProgress: false,
          progress: 0,
          hasError: !this.checkIfAllowed(file.name),
          errorMessage: this.getErrorMessage(file.name),
          canCancel: true,
          urlPreview: null
        };
        this.files.push(this.previewImages ? this.checkUrlPreview(fileToUpload) : fileToUpload);
      }
      if (this.directUpload) {
        this.uploadFiles();
      } else if (this.files.length > 1) {
        this.label = 'control.upload.label.plural';
      }
    };
    this.inputUpload.nativeElement.click();
  }

  /**
   * Cancel a file upload
   * @param file file to cancel
   */
  cancelFile(file: FileUploadModel) {
    this.removeFileFromArray(file);
  }

  /**
   * Retry a failed file
   * @param file file to retry
   */
  retryFile(file: FileUploadModel) {
    this.uploadFile(file);
    file.hasError = false;
  }

  /**
   * Function to upload multiple files
   */
  public uploadFiles() {
    this.inputUpload.nativeElement.value = '';
    this.files.forEach(file => {
      this.uploadFile(file);
    });
  }

  /**
   * Function to upload file
   * @param file file to uplaod
   */
  public uploadFile(file: FileUploadModel) {
    if (!file.hasError || file.canRetry) {
      const fd = new FormData();
      fd.append(this.param, file.data);
      fd.append('module', this.module);

      file.inProgress = true;
      file.progress = 0;
      file.hasError = false;
      file.canRetry = false;
      this.uploadStarted.emit();
      file.sub = this.http
        .post(`${this.appConfigService.getApi()}/filesystem/upload`, fd, { reportProgress: true, observe: 'events' })
        .pipe(
          map(event => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                file.progress = Math.round((event.loaded * 100) / event.total);
                break;
              case HttpEventType.Response:
                return event;
            }
          }),
          last(),
          catchError((error: HttpErrorResponse) => {
            file.inProgress = false;
            file.hasError = true;
            file.canRetry = true;
            file.errorMessage = 'Unknown error';
            this.uploadComplete.emit(error);
            return of(`${file.data.name} upload failed.`);
          })
        )
        .subscribe((event: HttpResponse<string>) => {
          if (event.ok) {
            this.removeFileFromArray(file);
            this.uploadComplete.emit(event.body);
            this.fileNameUploaded.emit(file.data.name);
          }
        });
    }
  }

  /**
   * Remove a file from the upload array
   * @param file file to remove
   */
  private removeFileFromArray(file: FileUploadModel) {
    const index = this.files.indexOf(file);
    if (index > -1) {
      this.files.splice(index, 1);
    }
  }

  /**
   * Get file extension
   * @param filename the name of the file to get the extension
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop();
  }

  /**
   * Get error message from file if not allowed
   * @param extension file extension to check
   */
  getErrorMessage(filename: string) {
    let fileExtension = this.getFileExtension(filename);
    if (this.whitelist.length === 1 && !this.checkWhitelist(filename)) {
      return this.translate.instant('control.upload.file.onlyAccepted', { fileType: this.whitelist[0] });
    } else if (!this.checkIfAllowed(filename)) {
      return this.translate.instant('control.upload.file.notAccepted', { fileType: fileExtension });
    }
  }

  /**
   * Check the whitelist to see if the file type is allowed
   * @param filename the name of the file to check
   */
  checkWhitelist(filename: string): boolean {
    return this.whitelist.includes(this.getFileExtension(filename));
  }

  /**
   * Check the blacklist to see if the file type is not allowed
   * @param filename the name of the file to check
   */
  checkBlacklist(filename: string): boolean {
    return this.blacklist.includes(this.getFileExtension(filename));
  }

  /**
   * Check if the file type is allowed
   * @param filename the name of the file to check
   */
  checkIfAllowed(filename: string) {
    return (
      (this.checkWhitelist(filename) || this.whitelist.length === 0) &&
      (!this.checkBlacklist(filename) || this.blacklist.length === 0)
    );
  }

  checkUrlPreview(file: FileUploadModel) {
    let reader = new FileReader();
    if (file.type.includes('image')) {
      reader.readAsDataURL(file.data);
      reader.onload = () => {
        file.urlPreview = reader.result;
      };
    }
    return file;
  }

  /**
   * Check if it is an allowed file type
   * @param filename The name of file to check
   */
  public checkIfImageAllowed(file: File) {
    return (
      this.getFileExtension(file.name) === FileTypeImage.JPEG ||
      this.getFileExtension(file.name) === FileTypeImage.JPG ||
      this.getFileExtension(file.name) === FileTypeImage.PNG
    );
  }

  // Get icons

  getFileIcon() {
    if (this.showIcon) {
      return Icons.FILE_UPLOAD;
    }
  }

  getAttachmentIcon() {
    return Icons.ATTACHMENT;
  }

  getCloseIcon() {
    return Icons.CLOSE;
  }

  getFileUploadIcon() {
    return Icons.FILE_UPLOAD;
  }

  getRefreshIcon() {
    return Icons.REFRESH;
  }
}
